import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShadowMode } from '../contexts/ShadowModeContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Menu, X, LogOut, Car, ChevronDown, Bell, Plus,
  LayoutDashboard, BarChart3, Calendar, FileText, Wallet, 
  ShieldCheck, User, Settings, Shield, Scale, Star, CalendarDays,
  AlertTriangle, Receipt, ChevronRight, CalendarPlus,
  Lock, MapPin, Sparkles, Zap, Megaphone, HeadphonesIcon, ShieldPlus
} from 'lucide-react';
import { OnboardingReminderBar } from './OnboardingChecklist';

// ============================================================================
// TIER CONFIGURATION — Single source of truth for host dashboard gating
// Maps feature flags to required tiers and display metadata.
// Feature flags come from backend entitlements.py via AuthContext.
// ============================================================================
const TIER_ORDER = ['free', 'basic', 'pro', 'premium'];

const TIER_DISPLAY = {
  free: { label: 'Free', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  basic: { label: 'Basic', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  pro: { label: 'Pro', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

// Feature-to-tier gating map. Items without a requiredFeature are always unlocked.
const FEATURE_GATE = {
  boost_listing: { requiredTier: 'basic', value: 'Increase your vehicle visibility in search results' },
  priority_support: { requiredTier: 'basic', value: 'Get faster response times from our support team' },
  advanced_analytics: { requiredTier: 'pro', value: 'Deep insights into your fleet performance and revenue trends' },
  gps_tracking: { requiredTier: 'pro', value: 'Real-time GPS tracking for all active trips' },
  ai_assistant: { requiredTier: 'premium', value: 'AI-powered pricing suggestions and fleet optimisation' },
  insurance_perks: { requiredTier: 'premium', value: 'Enhanced insurance coverage and premium claim support' },
};

function isTierSufficient(userTier, requiredTier) {
  return TIER_ORDER.indexOf(userTier || 'free') >= TIER_ORDER.indexOf(requiredTier || 'free');
}

/**
 * HostAppShell - Unified navigation shell for all Host pages
 * 
 * Features:
 * - Grouped collapsible sidebar navigation
 * - Swipe-from-left gesture on mobile
 * - Consistent header with hamburger menu
 * - Auto-expand active section
 * - One section expanded at a time on mobile
 * - Shadow mode banner compatibility
 */

// Sidebar navigation configuration
const getSidebarGroups = (notifications = 0) => [
  {
    title: 'Overview',
    icon: LayoutDashboard,
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/host' },
      { id: 'analytics', label: 'Analytics', icon: BarChart3, href: '/host/analytics' },
      { id: 'ai_assistant', label: 'AI Assistant', icon: Sparkles, href: '/host/ai-assistant', requiredFeature: 'ai_assistant' },
      { id: 'notifications', label: 'Notifications', icon: Bell, href: '/notifications', badge: notifications },
    ]
  },
  {
    title: 'Fleet',
    icon: Car,
    items: [
      { id: 'vehicles', label: 'My Vehicles', icon: Car, href: '/host/vehicles' },
      { id: 'rules', label: 'Host Rules', icon: Shield, href: '/host/rules' },
      { id: 'calendar', label: 'Availability', icon: CalendarDays, href: '/host/calendar' },
      { id: 'gps_tracking', label: 'GPS Tracking', icon: MapPin, href: '/host/gps', requiredFeature: 'gps_tracking' },
      { id: 'boost_listing', label: 'Boost Listing', icon: Megaphone, href: '/host/boost', requiredFeature: 'boost_listing' },
    ]
  },
  {
    title: 'Operations',
    icon: Calendar,
    items: [
      { id: 'bookings', label: 'Bookings', icon: Calendar, href: '/host/bookings' },
      { id: 'extensions', label: 'Extensions', icon: CalendarPlus, href: '/host/extensions' },
      { id: 'statements', label: 'Statements', icon: FileText, href: '/host/statements' },
      { id: 'payouts', label: 'Earnings & Payouts', icon: Wallet, href: '/host/payouts' },
      { id: 'claims', label: 'Claims Centre', icon: AlertTriangle, href: '/host/claims' },
    ]
  },
  {
    title: 'Legal & Trust',
    icon: Scale,
    items: [
      { id: 'agreements', label: 'Agreements', icon: FileText, href: '/host/agreements' },
      { id: 'protection', label: 'HireHub Protection', icon: ShieldCheck, href: '/host/protection' },
      { id: 'insurance_perks', label: 'Insurance Perks', icon: ShieldPlus, href: '/host/insurance-perks', requiredFeature: 'insurance_perks' },
    ]
  },
  {
    title: 'Account',
    icon: User,
    items: [
      { id: 'account', label: 'Account', icon: User, href: '/host/account' },
      { id: 'plan', label: 'Manage Plan', icon: Zap, href: '/host/plan' },
      { id: 'reviews', label: 'Reviews', icon: Star, href: '/host/reviews' },
      { id: 'priority_support', label: 'Support Center', icon: HeadphonesIcon, href: '/host/priority-support' },
    ]
  }
];

// Sidebar Group Component for collapsible sections
const SidebarGroup = ({ group, activeId, onItemClick, expandedGroup, onToggleGroup, isMobile, hasFeature, userTier, onLockedClick }) => {
  const isExpanded = expandedGroup === group.title;
  const hasActiveItem = group.items.some(item => item.id === activeId);
  const GroupIcon = group.icon;
  
  return (
    <div className="mb-1">
      {/* Group Header */}
      <button
        onClick={() => onToggleGroup(group.title)}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 rounded-lg
          transition-all duration-200 text-xs font-semibold uppercase tracking-wider
          ${hasActiveItem 
            ? 'text-[#004B8D] bg-blue-50' 
            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }
        `}
        data-testid={`host-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {GroupIcon && <GroupIcon className="w-4 h-4" />}
          <span>{group.title}</span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Group Items - Animated collapse */}
      <div 
        className={`
          overflow-hidden transition-all duration-200 ease-out
          ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="pl-2 pt-1 space-y-0.5">
          {group.items.map((item) => {
            const ItemIcon = item.icon;
            const gate = item.requiredFeature ? FEATURE_GATE[item.requiredFeature] : null;
            const isLocked = gate && !hasFeature(item.requiredFeature);
            const isNewlyUnlocked = gate && !isLocked && userTier !== 'free' && gate.requiredTier === userTier;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (isLocked) {
                    onLockedClick(item, gate);
                  } else if (!item.comingSoon) {
                    onItemClick(item);
                  }
                }}
                disabled={item.comingSoon}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 rounded-lg
                  transition-all duration-150 text-sm
                  ${item.comingSoon 
                    ? 'text-slate-400 cursor-not-allowed'
                    : isLocked
                      ? 'text-slate-400 hover:bg-slate-50 cursor-pointer'
                      : activeId === item.id 
                        ? 'bg-[#004B8D] text-white' 
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }
                `}
                data-testid={`host-nav-${item.id}`}
              >
                {ItemIcon && <ItemIcon className={`w-4 h-4 flex-shrink-0 ${isLocked ? 'opacity-50' : ''}`} />}
                <span className={`font-medium truncate ${isLocked ? 'opacity-60' : ''}`}>{item.label}</span>
                {isLocked && (
                  <Lock className="w-3.5 h-3.5 ml-auto flex-shrink-0 text-slate-400" />
                )}
                {item.comingSoon && (
                  <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0 h-4 text-slate-400 border-slate-200">
                    Soon
                  </Badge>
                )}
                {!isLocked && item.badge > 0 && (
                  <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                    {item.badge}
                  </span>
                )}
                {isNewlyUnlocked && activeId !== item.id && (
                  <Badge className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-green-100 text-green-700 border-green-200 border" data-testid={`new-badge-${item.id}`}>
                    New
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Locked Feature Prompt — shown when host clicks a tier-gated item
const LockedFeaturePrompt = ({ item, gate, userTier, onClose, onNavigatePlan }) => {
  if (!item || !gate) return null;
  const tierDisplay = TIER_DISPLAY[gate.requiredTier] || TIER_DISPLAY.pro;
  const currentDisplay = TIER_DISPLAY[userTier] || TIER_DISPLAY.free;
  const ItemIcon = item.icon;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
        data-testid="locked-feature-prompt"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            {ItemIcon ? <ItemIcon className="w-5 h-5 text-slate-400" /> : <Lock className="w-5 h-5 text-slate-400" />}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{item.label}</h3>
            <p className="text-xs text-slate-500">Available on <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${tierDisplay.color}`}>{tierDisplay.label}</span></p>
          </div>
        </div>
        
        <p className="text-sm text-slate-600 leading-relaxed">{gate.value}</p>
        
        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
          <div className="flex-1">
            <p className="text-xs text-slate-500">Your current tier</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold mt-0.5 ${currentDisplay.color}`}>{currentDisplay.label}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <div className="flex-1 text-right">
            <p className="text-xs text-slate-500">Required tier</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold mt-0.5 ${tierDisplay.color}`}>{tierDisplay.label}</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 h-10 text-sm" onClick={onClose} data-testid="locked-prompt-close">
            Got it
          </Button>
          <Button className="flex-1 h-10 text-sm bg-[#004B8D] hover:bg-[#003a6d]" onClick={() => { onClose(); onNavigatePlan(); }} data-testid="locked-prompt-learn-more">
            View Plans
          </Button>
        </div>
      </div>
    </div>
  );
};

export const HostAppShell = ({ 
  children, 
  title = 'Host Dashboard',
  showAddVehicle = false,
  activeId = null,
  hideOnboardingBar = false
}) => {
  const { user, logout, api, hasFeature, getSubscriptionTier } = useAuth();
  const { isInShadowMode } = useShadowMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [actionFlags, setActionFlags] = useState(0);
  const [criticalFlags, setCriticalFlags] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [lockedPrompt, setLockedPrompt] = useState(null); // { item, gate }
  
  const userTier = getSubscriptionTier();
  
  // Swipe gesture support
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const sidebarRef = useRef(null);

  // Determine active ID from current route if not provided
  const currentActiveId = activeId || getActiveIdFromPath(location.pathname);
  
  // Get sidebar groups with notification count (includes action flags)
  const totalAlerts = notifications + actionFlags;
  const sidebarGroups = getSidebarGroups(totalAlerts);

  // Fetch notification count and action flags
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/notifications/?unread_only=true');
        setNotifications(res.data.notifications?.length || 0);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    const fetchActionFlags = async () => {
      try {
        const res = await api.get('/host/action-flags');
        setActionFlags(res.data.count || 0);
        setCriticalFlags(res.data.critical_count || 0);
      } catch (error) {
        // Not a host or error - silently ignore
      }
    };
    
    fetchNotifications();
    fetchActionFlags();
    
    // Refresh action flags every 5 minutes
    const interval = setInterval(fetchActionFlags, 300000);
    
    // FIXED: Re-fetch notifications when user returns to page (visibility change)
    // This ensures unread count updates after marking notifications as read
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also re-fetch on focus (covers tab switching)
    const handleFocus = () => {
      fetchNotifications();
    };
    window.addEventListener('focus', handleFocus);
    
    // FIXED: Listen for custom event when notifications are marked as read
    // This enables immediate badge update without page reload
    const handleNotificationRead = () => {
      fetchNotifications();
    };
    window.addEventListener('notification-read', handleNotificationRead);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('notification-read', handleNotificationRead);
    };
  }, []);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-expand the group containing the active tab
  useEffect(() => {
    if (currentActiveId) {
      const groupWithActiveItem = sidebarGroups.find(group => 
        group.items.some(item => item.id === currentActiveId)
      );
      if (groupWithActiveItem) {
        setExpandedGroup(groupWithActiveItem.title);
      }
    }
  }, [currentActiveId]);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Swipe gesture handlers
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;
      
      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Only trigger if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        // Swipe right from left edge to open sidebar
        if (deltaX > 0 && touchStartX.current < 30 && !sidebarOpen) {
          setSidebarOpen(true);
          touchStartX.current = null;
          touchStartY.current = null;
        }
        // Swipe left to close sidebar
        if (deltaX < 0 && sidebarOpen) {
          setSidebarOpen(false);
          touchStartX.current = null;
          touchStartY.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      touchStartX.current = null;
      touchStartY.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [sidebarOpen]);

  const handleNavClick = (item) => {
    if (item.href) {
      navigate(item.href);
    }
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleToggleGroup = (groupTitle) => {
    // On mobile, only one group expanded at a time
    if (isMobile) {
      setExpandedGroup(prev => prev === groupTitle ? null : groupTitle);
    } else {
      setExpandedGroup(prev => prev === groupTitle ? null : groupTitle);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLockedClick = (item, gate) => {
    setLockedPrompt({ item, gate });
  };

  return (
    <div className={`min-h-screen bg-slate-50 ${isInShadowMode ? 'pt-[68px]' : ''}`}>
      {/* Onboarding Reminder Bar - Shows above header */}
      {!hideOnboardingBar && <OnboardingReminderBar />}
      
      {/* Mobile Header */}
      <header className={`lg:hidden fixed left-0 right-0 z-40 bg-white border-b border-slate-100 px-4 py-3 safe-area-inset-top ${isInShadowMode ? 'top-[68px]' : 'top-0'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarOpen(true)} 
              className="text-slate-700 hover:bg-slate-100"
              data-testid="host-mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="font-heading font-semibold text-lg text-slate-900 truncate">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => navigate('/notifications')}
              data-testid="notification-bell"
            >
              <Bell className={`w-5 h-5 ${criticalFlags > 0 ? 'text-red-500' : 'text-slate-600'}`} />
              {totalAlerts > 0 && (
                <span className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs rounded-full flex items-center justify-center ${criticalFlags > 0 ? 'bg-red-600 animate-pulse' : 'bg-amber-500'}`}>
                  {totalAlerts}
                </span>
              )}
            </Button>
            {showAddVehicle && (
              <Button 
                size="sm" 
                className="bg-[#004B8D] hover:bg-[#003a6d] h-9"
                onClick={() => navigate('/host/vehicles/new')}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar Backdrop */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
          data-testid="host-sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`
          fixed left-0 z-50 w-72 max-w-[85vw]
          bg-white border-r border-slate-100
          transform transition-transform duration-300 ease-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isInShadowMode ? 'top-[68px] bottom-0' : 'inset-y-0'}
        `}
        data-testid="host-sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 lg:p-5 border-b border-slate-100 flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="host-sidebar-logo"
            >
              <div className="w-10 h-10 bg-[#004B8D] rounded-xl flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base text-slate-900">HireHub</h1>
                <p className="text-xs text-slate-500">Host Dashboard</p>
              </div>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-slate-500 hover:bg-slate-100 flex-shrink-0" 
              onClick={() => setSidebarOpen(false)}
              data-testid="host-close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* User Info + Tier Badge */}
          {user && (
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#004B8D] rounded-full flex items-center justify-center text-sm text-white font-medium flex-shrink-0">
                  {user.full_name?.charAt(0) || user.email?.charAt(0) || 'H'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 truncate">{user.full_name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{user.email}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2" data-testid="host-tier-badge">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${(TIER_DISPLAY[userTier] || TIER_DISPLAY.free).color}`}>
                  <Zap className="w-3 h-3" />
                  {(TIER_DISPLAY[userTier] || TIER_DISPLAY.free).label} Plan
                </span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 overflow-y-auto">
            <div className="space-y-1">
              {sidebarGroups.map((group) => (
                <SidebarGroup
                  key={group.title}
                  group={group}
                  activeId={currentActiveId}
                  onItemClick={handleNavClick}
                  expandedGroup={expandedGroup}
                  onToggleGroup={handleToggleGroup}
                  isMobile={isMobile}
                  hasFeature={hasFeature}
                  userTier={userTier}
                  onLockedClick={handleLockedClick}
                />
              ))}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-slate-100">
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50 h-10"
              onClick={handleLogout}
              data-testid="host-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Sign Out</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Desktop Header */}
      <header className="hidden lg:flex fixed top-0 left-72 right-0 z-30 bg-white border-b border-slate-100 px-6 py-4 items-center justify-between">
        <h1 className="font-heading text-xl font-semibold text-slate-900">{title}</h1>
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            onClick={() => navigate('/notifications')}
            data-testid="notification-bell-desktop"
          >
            <Bell className={`w-5 h-5 ${criticalFlags > 0 ? 'text-red-500' : 'text-slate-600'}`} />
            {totalAlerts > 0 && (
              <span className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs rounded-full flex items-center justify-center ${criticalFlags > 0 ? 'bg-red-600 animate-pulse' : 'bg-amber-500'}`}>
                {totalAlerts}
              </span>
            )}
          </Button>
          {showAddVehicle && (
            <Button 
              className="bg-[#004B8D] hover:bg-[#003a6d]"
              onClick={() => navigate('/host/vehicles/new')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className={`lg:ml-72 min-h-screen ${isInShadowMode ? 'pt-[82px] lg:pt-[141px]' : 'pt-14 lg:pt-[73px]'}`}>
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Locked Feature Prompt Overlay */}
      {lockedPrompt && (
        <LockedFeaturePrompt
          item={lockedPrompt.item}
          gate={lockedPrompt.gate}
          userTier={userTier}
          onClose={() => setLockedPrompt(null)}
          onNavigatePlan={() => navigate('/host/plan')}
        />
      )}
    </div>
  );
};

// Helper function to determine active ID from pathname
function getActiveIdFromPath(pathname) {
  const pathMap = {
    '/host': 'dashboard',
    '/host/analytics': 'analytics',
    '/host/vehicles': 'vehicles',
    '/host/rules': 'rules',
    '/host/calendar': 'calendar',
    '/host/protection': 'protection',
    '/host/bookings': 'bookings',
    '/host/extensions': 'extensions',
    '/host/statements': 'statements',
    '/host/payouts': 'payouts',
    '/host/claims': 'claims',
    '/host/agreements': 'agreements',
    '/host/account': 'account',
    '/host/plan': 'plan',
    '/host/reviews': 'reviews',
    '/notifications': 'notifications',
    '/profile': 'account',
  };
  
  // Check exact matches first
  if (pathMap[pathname]) {
    return pathMap[pathname];
  }
  
  // Check prefix matches for nested routes
  if (pathname.startsWith('/host/vehicles')) return 'vehicles';
  if (pathname.startsWith('/host/')) return 'dashboard';
  if (pathname.startsWith('/host/')) return 'dashboard';
  
  return 'dashboard';
}

export default HostAppShell;
